import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuth from './useAuth';
import PATH_PAGE from '@/router/path';
import { getAuthToken } from '@/lib/cookies';
import { DEMO_MODE } from '@/constants';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  if (DEMO_MODE) {
    return <>{children}</>;
  }

  const { session, isLoading, checkCookieAuth } = useAuth();
  const location = useLocation();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    async function verifyAuth() {
      // If we already have a session, consider authenticated
      if (session) {
        setIsAuthenticated(true);
        setIsVerifying(false);
        return;
      }

      // Check for auth token in cookies if no session
      const cookieAuthToken = getAuthToken();
      if (cookieAuthToken) {
        // Attempt to verify the cookie token
        const isValid = await checkCookieAuth();
        setIsAuthenticated(isValid);
      } else {
        setIsAuthenticated(false);
      }
      
      setIsVerifying(false);
    }

    // Only try cookie auth if Supabase auth is done loading
    if (!isLoading) {
      verifyAuth();
    }
  }, [session, isLoading, checkCookieAuth]);

  if (isLoading || isVerifying) {
    return <div />; // Loading state
  }

  if (!isAuthenticated) {
    return (
      <Navigate to={PATH_PAGE.auth.signIn} state={{ from: location }} replace />
    );
  }

  return <>{children}</>;

};

export default ProtectedRoute;
